import { gql } from "@/gql/__generated__";
import { useLazyQuery } from "@apollo/client";
import { useCallback } from "react";
import { useCreateApp } from "./createApp";

const APP = gql(`
  query READ_APP($organizationSlug: String!, $appSlug: String!) {
    app(organizationSlug: $organizationSlug, appSlug: $appSlug) {
      ... on App {
        id
      }
    }
  }
`);

type CreateOrReadAppVariables = {
  organizationSlug: string;
  appSlug: string;
  displayName: string;
  description: string;
};

export const useCreateOrReadApp = () => {
  const [readApp] = useLazyQuery(APP);
  const [createApp] = useCreateApp();

  const createOrReadApp = useCallback(
    async ({
      appSlug,
      organizationSlug,
      displayName,
      description = ""
    }: CreateOrReadAppVariables): Promise<{ id: string }> => {
      const readAppResponse = await readApp({
        variables: {
          organizationSlug,
          appSlug
        },
        fetchPolicy: "network-only"
      });

      if (readAppResponse.data?.app) return readAppResponse.data.app;
      if (readAppResponse.error?.message !== "app not found")
        throw new Error(readAppResponse.error?.message);

      const createAppResponse = await createApp({
        variables: {
          organizationSlug,
          appData: {
            appSlug,
            displayName,
            description
          }
        }
      });

      if (createAppResponse.data?.appCreate)
        return createAppResponse.data.appCreate;
      if (createAppResponse.errors) throw createAppResponse.errors[0];
      throw new Error(
        `Unknown error when trying to create app with AppSlug: ${appSlug}`
      );
    },
    [createApp, readApp]
  );

  return createOrReadApp;
};
